/**
 * Page archives (spec §8.20, §10.4): start an export or an import, poll the transfer, download.
 * Export and import run on the server; the client only waits for them.
 */
import { api, unwrap, ApiError } from "../api/client";
import type { components } from "../api/schema";

export type Transfer = components["schemas"]["TransferOut"];

export interface ImportResult {
  docId: string;
  title: string;
  blocks: number;
  files: number;
  concepts: { matched: number; created: number };
  railStyles: number;
  sequences: number;
  workspaceLinks: number;
  notes: string[];
}

const POLL_MS = 1000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Poll until the transfer is done; a failed one throws with the server's reason. */
export async function waitForTransfer(t: Transfer): Promise<Transfer> {
  let current = t;
  while (current.state === "pending" || current.state === "running") {
    await sleep(POLL_MS);
    current = unwrap<Transfer>(await api.GET("/api/transfers/{transfer_id}", { params: { path: { transfer_id: current.id } } }));
  }
  if (current.state === "failed") throw new ApiError(422, current.error ?? "failed");
  return current;
}

export async function exportPage(docId: string): Promise<Transfer> {
  const t = unwrap<Transfer>(await api.POST("/api/pages/{doc_id}/export", { params: { path: { doc_id: docId } } }));
  return waitForTransfer(t);
}

export const downloadUrl = (t: Transfer) => `/api/transfers/${t.id}/download`;

function csrfToken(): string {
  return document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)?.[1] ?? "";
}

/** Upload the archive as the request body, reporting progress (0..1), then wait for the import. */
export function importPage(workspaceId: string, file: File, onProgress: (fraction: number) => void): Promise<Transfer> {
  return new Promise<Transfer>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/workspaces/${workspaceId}/imports`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/zip");
    xhr.setRequestHeader("X-CSRFToken", csrfToken());
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError(0, "import.uploadFailed")) // i18n keys: the button translates them (§8.23);
    xhr.onload = () => {
      let body: { detail?: string } & Partial<Transfer> = {};
      try {
        body = JSON.parse(xhr.responseText) as typeof body;
      } catch {
        /* not JSON */
      }
      if (xhr.status === 200 || xhr.status === 202) resolve(waitForTransfer(body as Transfer));
      else reject(new ApiError(xhr.status, body.detail ?? (xhr.status === 413 ? "import.tooLarge" : xhr.statusText)));
    };
    xhr.send(file);
  });
}
